// src/pages/Projects/ProjectTeamList.jsx
// -----------------------------------------------------------------------------
// Component screen managing Project Teams (Project_Team_TBL)
// Performs full CRUD (Read, Create, Update, Delete) via projectTeamService.
// -----------------------------------------------------------------------------
import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import projectTeamService from '../../api/projectTeamService';
import projectService from '../../api/projectService';
import useTableData from '../../hooks/useTableData';
import TableList from '../../components/common/TableList';
import TableForm from '../../components/common/TableForm';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';

export default function ProjectTeamList() {
  const [projects, setProjects] = useState([]);
  const [searchParams] = useSearchParams();
  const projectParam = searchParams.get('projectID');

  const [selectedProjectFilter, setSelectedProjectFilter] = useState(projectParam || '');

  // Sync project filter if URL parameters change
  useEffect(() => {
    if (projectParam) {
      setSelectedProjectFilter(projectParam);
    }
  }, [projectParam]);

  // Fetch all projects to populate dropdown selection in form & filter
  useEffect(() => {
    projectService
      .getAll()
      .then((res) => setProjects(res.data || []))
      .catch(() => {});
  }, []);

  const {
    data, // Already filtered by searchTerm and sorted
    loading,
    submitting,
    deleting,
    serverErrors,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,
    handleSave,
    handleDelete,
  } = useTableData(projectTeamService, {
    idKey: 'teamID',
    itemName: 'Project Team',
    initialSort: { key: 'teamID', direction: 'asc' },
  });

  // Local pagination states (to properly support custom Project filter)
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Apply Project ID filter to the searched and sorted data
  const filteredByProject = useMemo(() => {
    if (!selectedProjectFilter) return data;
    return data.filter((item) => Number(item.projectID) === Number(selectedProjectFilter));
  }, [data, selectedProjectFilter]);

  // Slice for current page
  const paginatedTeams = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredByProject.slice(startIndex, startIndex + pageSize);
  }, [filteredByProject, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredByProject.length / pageSize) || 1;
  const totalRecords = filteredByProject.length;

  // Reset pagination if search or project filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedProjectFilter]);

  // Schema configuration for Project_Team_TBL
  const projectTeamFields = useMemo(() => [
    {
      name: 'teamID',
      label: 'ID',
      type: 'integer',
      isPrimaryKey: true,
      readOnly: true,
    },
    {
      name: 'projectID',
      label: 'Project',
      type: 'select',
      required: true,
      options: projects.map((p) => ({ label: `${p.projectCode} — ${p.projectName}`, value: p.projectID })),
    },
    {
      name: 'teamName',
      label: 'Team Name',
      type: 'varchar',
      required: true,
      maxLength: 100,
      placeholder: 'e.g. Frontend Team, QA Verification Squad',
    },
  ], [projects]);

  const columns = [
    { key: 'teamID', label: 'ID', sortable: true, width: '80px' },
    {
      key: 'projectID',
      label: 'Project',
      sortable: true,
      render: (row) => {
        const found = projects.find((p) => Number(p.projectID) === Number(row.projectID));
        return found ? `${found.projectCode} — ${found.projectName}` : `Project ID: ${row.projectID}`;
      },
    },
    { key: 'teamName', label: 'Team Name', sortable: true },
  ];

  const renderHeaderActions = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <label htmlFor="projectFilterSelect" style={{ fontWeight: '500', fontSize: '13px', margin: 0, whiteSpace: 'nowrap' }}>
          Filter by Project:
        </label>
        <select
          id="projectFilterSelect"
          value={selectedProjectFilter}
          onChange={(e) => setSelectedProjectFilter(e.target.value)}
          style={{
            padding: '6px 12px',
            borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--color-border)',
            fontSize: '13px',
            backgroundColor: 'var(--color-bg)',
            color: 'var(--color-text)',
            minWidth: '180px'
          }}
        >
          <option value="">All Projects</option>
          {projects.map((p) => (
            <option key={p.projectID} value={p.projectID}>
              {p.projectCode} — {p.projectName}
            </option>
          ))}
        </select>
      </div>
      {selectedProjectFilter && (
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setSelectedProjectFilter('')}
          style={{ padding: '6px 12px', fontSize: '12px' }}
        >
          Clear
        </button>
      )}
    </div>
  );

  return (
    <div>
      <div className="page-header" style={{ marginBottom: '20px' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px' }}>Project Teams</h1>
          <div className="subtitle" style={{ color: 'var(--color-text-muted)', fontSize: '13px', marginTop: '4px' }}>
            Configure and manage the workgroup teams assigned to each software project.
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + New Team
        </button>
      </div>

      {/* Main Grid Table List */}
      <TableList
        columns={columns}
        data={filteredByProject}
        paginatedData={paginatedTeams}
        loading={loading}
        idKey="teamID"
        emptyMessage="No project teams configured."
        // Search & Filter
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        renderHeaderActions={renderHeaderActions}
        // Sorting
        sortConfig={sortConfig}
        onSort={handleSort}
        // Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Row Actions
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      {/* Reusable Form Modal */}
      {isFormOpen && (
        <TableForm
          fields={projectTeamFields}
          initialValues={selectedRecord || { projectID: '', teamName: '' }}
          mode={formMode}
          isOpen={isFormOpen}
          onSubmit={handleSave}
          onCancel={closeFormModal}
          submitting={submitting}
          serverErrors={serverErrors}
          itemName="Project Team"
        />
      )}

      {/* Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete Project Team"
        itemLabel={selectedRecord ? `project team '${selectedRecord.teamName}'` : 'Project Team'}
        deleting={deleting}
      />
    </div>
  );
}
